import { createFileRoute } from "@tanstack/react-router";
import { useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useServerFn } from "@tanstack/react-start";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import {
  adminDeleteCoupon,
  adminListCoupons,
  adminSaveCoupon,
} from "@/lib/admin.functions";
import { privatePageMetadata } from "@/lib/seo";

export const Route = createFileRoute("/nc/live/adm/coupons")({
  head: () =>
    privatePageMetadata(
      "/nc/live/adm/coupons",
      "Coupons | Control room",
      "Internal administration.",
    ),
  component: AdminCoupons,
});

type CouponInput = {
  id?: string;
  code: string;
  description?: string;
  kind: "percent" | "heat";
  percentOff: number;
  heatAmount: number;
  maxRedemptions: number;
  expiresAt?: string;
  isActive: boolean;
};

function AdminCoupons() {
  const qc = useQueryClient();
  const list = useServerFn(adminListCoupons);
  const save = useServerFn(adminSaveCoupon);
  const remove = useServerFn(adminDeleteCoupon);

  const { data: coupons = [] } = useQuery({
    queryKey: ["adm-coupons"],
    queryFn: () => list({ data: undefined }),
  });

  const saveMut = useMutation({
    mutationFn: (input: CouponInput) => save({ data: input }),
    onSuccess: () => {
      toast.success("Coupon saved.");
      void qc.invalidateQueries({ queryKey: ["adm-coupons"] });
    },
    onError: (e) => toast.error(e instanceof Error ? e.message : "Save failed."),
  });

  const deleteMut = useMutation({
    mutationFn: (id: string) => remove({ data: { id } }),
    onSuccess: () => {
      toast.success("Coupon removed.");
      void qc.invalidateQueries({ queryKey: ["adm-coupons"] });
    },
  });

  const [form, setForm] = useState<CouponInput>({
    code: "",
    kind: "percent",
    percentOff: 10,
    heatAmount: 0,
    maxRedemptions: 0,
    isActive: true,
  });

  return (
    <div className="space-y-6">
      <section className="card-surface p-4">
        <h2 className="font-display text-lg font-bold">New coupon</h2>
        <div className="mt-3 grid gap-3 md:grid-cols-3">
          <div className="space-y-1">
            <Label>Code</Label>
            <Input
              value={form.code}
              onChange={(e) => setForm({ ...form, code: e.target.value.toUpperCase() })}
              placeholder="HOT10"
            />
          </div>
          <div className="space-y-1">
            <Label>Type</Label>
            <select
              className="h-10 w-full rounded-md border border-input bg-background px-3 text-sm"
              value={form.kind}
              onChange={(e) => setForm({ ...form, kind: e.target.value as "percent" | "heat" })}
            >
              <option value="percent">Percent off</option>
              <option value="heat">Bonus Heat</option>
            </select>
          </div>
          <div className="space-y-1">
            <Label>{form.kind === "percent" ? "Percent off" : "Heat granted"}</Label>
            <Input
              type="number"
              value={form.kind === "percent" ? form.percentOff : form.heatAmount}
              onChange={(e) =>
                setForm(
                  form.kind === "percent"
                    ? { ...form, percentOff: Number(e.target.value) }
                    : { ...form, heatAmount: Number(e.target.value) },
                )
              }
            />
          </div>
          <div className="space-y-1">
            <Label>Max redemptions (0 = unlimited)</Label>
            <Input
              type="number"
              value={form.maxRedemptions}
              onChange={(e) => setForm({ ...form, maxRedemptions: Number(e.target.value) })}
            />
          </div>
          <div className="space-y-1">
            <Label>Expires</Label>
            <Input
              type="date"
              value={form.expiresAt ?? ""}
              onChange={(e) => setForm({ ...form, expiresAt: e.target.value })}
            />
          </div>
          <div className="space-y-1">
            <Label>Description</Label>
            <Input
              value={form.description ?? ""}
              onChange={(e) => setForm({ ...form, description: e.target.value })}
            />
          </div>
        </div>
        <Button
          variant="hot"
          className="mt-4"
          disabled={form.code.length < 3}
          onClick={() => saveMut.mutate(form)}
        >
          Create coupon
        </Button>
      </section>

      <section>
        <h2 className="font-display text-lg font-bold">Coupons</h2>
        <div className="mt-3 space-y-2">
          {coupons.length === 0 && <p className="text-sm text-muted-foreground">No coupons yet.</p>}
          {coupons.map((c) => (
            <div key={c.id} className="card-surface flex flex-wrap items-center gap-3 p-3">
              <div className="min-w-0 flex-1">
                <p className="font-semibold">{c.code}</p>
                <p className="text-xs text-muted-foreground">
                  {c.kind === "percent" ? `${c.percent_off}% off` : `${c.heat_amount} Heat`} ·{" "}
                  {c.redeemed_count}/{c.max_redemptions || "∞"} used
                  {c.expires_at ? ` · expires ${new Date(c.expires_at).toLocaleDateString()}` : ""}
                </p>
              </div>
              <label className="flex items-center gap-2 text-xs">
                <Switch
                  checked={c.is_active}
                  onCheckedChange={(v) =>
                    saveMut.mutate({
                      id: c.id,
                      code: c.code,
                      kind: c.kind as "percent" | "heat",
                      percentOff: c.percent_off,
                      heatAmount: c.heat_amount,
                      maxRedemptions: c.max_redemptions,
                      isActive: v,
                      ...(c.description ? { description: c.description } : {}),
                      ...(c.expires_at ? { expiresAt: c.expires_at } : {}),
                    })
                  }
                />
                Active
              </label>
              <Button
                size="sm"
                variant="ghost"
                className="text-destructive"
                onClick={() => deleteMut.mutate(c.id)}
              >
                Delete
              </Button>
            </div>
          ))}
        </div>
      </section>
    </div>
  );
}
